import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthenticatedMember, UpsertGuessInput, UpsertTastingInput } from '@cercle/shared';
import { createApi, type Api } from './api/client';
import { apiBaseUrl, FetchHttpClient } from './http/fetch-http-client';
import { indexedDbStorage } from './offline/idb';
import { OfflineQueue, verdictForResponse } from './offline/queue';
import { keyFor, type SyncStatus } from './offline/types';

/**
 * Composition root du front : le seul endroit qui connaît les adaptateurs concrets.
 *
 * Deux états sont réellement globaux et vivent ici :
 *  - la session du membre, dont dépend tout le routage ;
 *  - la file offline, dont l'état s'affiche en en-tête de presque tous les écrans.
 *
 * Tout le reste est de l'état local d'écran : y mettre un store global serait de la
 * cérémonie sans bénéfice pour une application de douze écrans.
 */
interface AppContextValue {
  api: Api;
  member: AuthenticatedMember | null;
  sessionChecked: boolean;
  setMember: (member: AuthenticatedMember | null) => void;
  signOut: () => Promise<void>;
  sync: SyncStatus;
  queueTasting: (input: UpsertTastingInput) => Promise<void>;
  queueGuess: (input: UpsertGuessInput) => Promise<void>;
  /** La note encore en file pour ce rhum : plus récente, par construction, que le serveur. */
  pendingTasting: (sessionId: string, rumId: string) => Promise<UpsertTastingInput | null>;
  flushQueue: () => Promise<void>;
  acknowledgeDiscarded: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

const INITIAL_SYNC: SyncStatus = {
  state: 'idle',
  pending: 0,
  discarded: 0,
  lastSyncedAt: null,
};

export function AppProvider({ children }: { children: ReactNode }): ReactNode {
  const [member, setMember] = useState<AuthenticatedMember | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sync, setSync] = useState<SyncStatus>(INITIAL_SYNC);

  // `useRef` et non `useState` : recréer le client HTTP à chaque rendu réinitialiserait
  // le verrou de rafraîchissement single-flight.
  const httpRef = useRef(new FetchHttpClient(apiBaseUrl(), () => setMember(null)));
  const api = useMemo(() => createApi(httpRef.current), []);

  const queueRef = useRef<OfflineQueue | null>(null);
  queueRef.current ??= new OfflineQueue({
    storage: indexedDbStorage,
    onChange: setSync,
    send: async (operation) => {
      const path = operation.kind === 'tasting' ? '/tastings' : '/guesses';
      const { status, body } = await httpRef.current.raw(path, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(operation.payload),
      });
      return verdictForResponse(status, (body as { code?: string } | null)?.code);
    },
  });
  const queue = queueRef.current;

  // Reprise de session au démarrage : le cookie httpOnly n'est pas lisible en JS,
  // seul un appel peut dire si la session tient encore.
  useEffect(() => {
    let cancelled = false;
    api.auth
      .me()
      .then((current) => {
        if (!cancelled) setMember(current);
      })
      .catch(() => {
        if (!cancelled) setMember(null);
      })
      .finally(() => {
        if (!cancelled) setSessionChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Rejeu à la reconnexion, et au retour de l'onglet au premier plan : sur mobile,
  // `online` ne se déclenche pas toujours quand l'application était en arrière-plan.
  useEffect(() => {
    const flush = () => {
      void queue.flush();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) flush();
    };
    const onOffline = () => {
      void queue.status().then(setSync);
    };

    window.addEventListener('online', flush);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    void queue.status().then(setSync);
    if (navigator.onLine) flush();

    return () => {
      window.removeEventListener('online', flush);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [queue]);

  const queueTasting = useCallback(
    async (input: UpsertTastingInput) => {
      await queue.enqueue({ kind: 'tasting', payload: input });
      if (navigator.onLine) await queue.flush();
    },
    [queue],
  );

  const queueGuess = useCallback(
    async (input: UpsertGuessInput) => {
      await queue.enqueue({ kind: 'guess', payload: input });
      if (navigator.onLine) await queue.flush();
    },
    [queue],
  );

  const pendingTasting = useCallback(
    async (sessionId: string, rumId: string) => {
      const operation = await queue.find(keyFor('tasting', sessionId, rumId));
      return operation?.kind === 'tasting' ? operation.payload : null;
    },
    [queue],
  );

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setMember(null);
    }
  }, [api]);

  const value = useMemo<AppContextValue>(
    () => ({
      api,
      member,
      sessionChecked,
      setMember,
      signOut,
      sync,
      queueTasting,
      queueGuess,
      pendingTasting,
      flushQueue: () => queue.flush(),
      acknowledgeDiscarded: () => {
        queue.acknowledgeDiscarded();
        void queue.status().then(setSync);
      },
    }),
    [api, member, sessionChecked, signOut, sync, queueTasting, queueGuess, pendingTasting, queue],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp doit être utilisé dans un <AppProvider>.');
  return context;
}

export function useApi(): Api {
  return useApp().api;
}
